// fetchAccountLinks.js
'use strict';
import fetch from '../resource/customFetch.js';

import { AuthId } from '../resource/loginRegisterConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import setMeData from '../action/setMeData.js';
import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import { setEmail, isDevEnvironment } from '../resource/mixpanel.js';

const selectPathInMe = 'accountLinks';
const selectPath = ['me', selectPathInMe];

/**
 * Fetch account links
 * @kind action
 * @param {string} {version} - the version of account list API
 * By default, different account types only have one record each.
 * However, an user can bind multiple WebAuthn passkeys,
 * that's why a new version of API is needed.
 * Check out the following documentation for the difference between v1 and v2:
 * https://www.notion.so/swaglive/Social-Account-9ed9116e98e1464b96e4ca4bfaa32bc6#3c57365527844db485c7a436251b25e9
 * @return {Promise} Action promise.
 */
const fetchAccountLinks =
  ({ version } = {}) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    const currentAccountLinks = getMeData(getState(), 'accountLinks');
    const isFetching = getNetworkingData(getState(), selectPath, 'isFetching');
    if (!token || isFetching) {
      return { type: '' };
    }

    const fetchOptions = {
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };
    const url = getResourceUrl({ endpoint: '/me/accounts' });
    if (version) {
      url.searchParams.set('v', version);
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const sourceData = await response.json();
      let accountLinks;

      // It's mainly for webAuthn.
      // Convert to v1 format as much as possible.
      if (version === '2') {
        accountLinks = Object.create(null);
        const passkeys = [];
        (sourceData || []).forEach(accountLink => {
          if (AuthId.WEB_AUTHN === accountLink.name) {
            passkeys.push(accountLink);
          } else {
            accountLinks[accountLink.name] = accountLink;
          }
        });
        accountLinks[AuthId.WEB_AUTHN] = {
          // name, id, displayName are for v1 format,
          // and they're actually dummy for webauthn except for the name
          name: AuthId.WEB_AUTHN,
          id: AuthId.WEB_AUTHN,
          displayName: AuthId.WEB_AUTHN,
          // passkeys is for webauthn itself (to showcase multiple keys)
          passkeys,
        };
      } else {
        accountLinks = sourceData;
      }

      const data = {
        ...accountLinks,
        'apple-id': accountLinks['apple-id'] || accountLinks.apple,
      };

      if (!version) {
        // Check `currentAccountLinks` first.
        // If it's not empty, it might come from either v1 or v2,
        // and we shouldn't override it if it has a chance to be v2.
        // (v1 only cares if webauthn is connected/associated;
        // whereas v2 actually needs real data, such as passkeys, which v1 doesn't own)
        if (data[AuthId.WEB_AUTHN] && currentAccountLinks?.[AuthId.WEB_AUTHN]) {
          data[AuthId.WEB_AUTHN] = currentAccountLinks[AuthId.WEB_AUTHN];
        }
      }

      const email = accountLinks.email?.id;

      if (isDevEnvironment && email) {
        setEmail({ email });
      }

      dispatch(setMeData({ selectPath: selectPathInMe, value: data }));
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchAccountLinks;
